/**
 * The month's trade, in order. The one entry point `turn.ts` calls.
 *
 * Built as its own file for the same reason `diploturn.ts` is: the pieces
 * (`trade.ts`, `merchants.ts`, `tradeai.ts`) each know one thing, and the order
 * they happen in is a decision of its own that deserves somewhere to be written
 * down. It also keeps the import graph a tree — `trade.ts` does not know the
 * council exists, and the council does not know when it runs.
 *
 * The order, and the argument for each step:
 *
 *   1. **Merchants walk.** Anyone who arrives this month is working before the
 *      month is priced, so a merchant sent three months ago pays out on the
 *      month he was promised to.
 *   2. **Value.** Read off the province table as it stands *after* `doIncome`
 *      has settled unrest and refilled garrisons, so trade is scaled by exactly
 *      the board tax was scaled by.
 *   3. **Control drifts.** Before the flow, so this month's income is paid at
 *      this month's shares rather than last month's.
 *   4. **Flow, and everybody is paid.**
 *   5. **The council sits**, last, so the AI's merchants are placed against the
 *      result of the month rather than its opening position. That is the same
 *      argument `turn.ts` makes for putting the military council at the end of
 *      the turn, and it is what makes an opponent look like it is paying
 *      attention.
 *
 * This runs once a month of simulated time — in the `Income` phase of the
 * council — and never in a tick. Cost, at 127 provinces, 16 nodes and 24 crowns:
 * one province scan, one army scan, sixteen nodes by twenty-four nations twice,
 * and a merchant sitting for a third of the crowns. See the note at the top of
 * `trade.ts`.
 */

import type { World } from '../world.ts';
import type { CampaignState } from './types.ts';
import { CONTROL_DRIFT } from './tradetypes.ts';
import { nodeLeader, recomputeControl, recomputeLocalValue, runFlow } from './trade.ts';
import { advanceMerchants, logMarketTurnover } from './merchants.ts';
import { tradeCouncil } from './tradeai.ts';

/**
 * One month of trade. Called from `TurnPhase.Income` in `turn.ts`, after
 * `doIncome`.
 *
 * Safe to call on a campaign that predates trade entirely: a save with no node
 * graph in it loads with an empty one and this returns immediately.
 */
export function tradeTurn(w: World): void {
  const c = w.campaign;
  if (!c || c.over) return;
  const t = c.trade;
  if (!t || t.nodes.length === 0) return;

  advanceMerchants(c);
  recomputeLocalValue(c);

  const before = leaders(c);
  recomputeControl(c, CONTROL_DRIFT);
  announceTurnovers(c, before);

  runFlow(c);
  tradeCouncil(c);
}

/**
 * Who had the whip hand in each market before control moved.
 *
 * A local, not a field. The comparison is only ever made between two adjacent
 * months inside this one function, so storing it would be storing a value whose
 * lifetime is four lines — and it would be state that has to be hashed, saved
 * and migrated for the sake of one chronicle line.
 */
function leaders(c: CampaignState): number[] {
  return c.trade.nodes.map((n) => nodeLeader(c, n.id).nation);
}

/**
 * Say something when a market changes hands, but only about the player's.
 *
 * Losing a node is the one thing in this system that happens *to* the player
 * rather than because of them, and the fairness rule the rest of this layer
 * keeps — everything is visible before it lands — would be broken by an income
 * that quietly fell by a third with nothing on screen to explain it. Two dozen
 * crowns trading markets among themselves is noise, and `MAX_LOG` is forty-eight
 * lines; the chronicle stays about the player.
 */
function announceTurnovers(c: CampaignState, before: number[]): void {
  for (const node of c.trade.nodes) {
    const was = before[node.id];
    const now = nodeLeader(c, node.id).nation;
    if (was === now) continue;
    logMarketTurnover(c, node.id, was, now);
  }
}
